/*
  ------------------------------------------------------------
  BERIT PRODUCT — custom variant dropdowns
  Replaces the native browser dropdowns (size, oura model) with a
  custom panel in BERIT's style (Sophie Buhai feel): the value on the
  left, a stock status on the right, thin dividers, a caret that flips.

  It does NOT reinvent the cart. Each custom dropdown sits on top of
  Dawn's real <select>, which stays in the page (just hidden). Choosing
  an option sets that hidden select and fires its 'change' event, so all
  of Dawn's price-updates-on-select and add-to-cart logic keeps working.

  When Dawn re-renders the variant area after a selection (to refresh
  which combinations are available), a small observer rebuilds the
  custom dropdowns to match.
  ------------------------------------------------------------
*/
(() => {
  const WRAPPER_SELECTOR = '.berit-product__pickers .product-form__input--dropdown';

  // Close every open custom dropdown (optionally leaving one open).
  function closeAll(except) {
    document.querySelectorAll('.berit-select.is-open').forEach((el) => {
      if (el === except) return;
      el.classList.remove('is-open');
      const trigger = el.querySelector('.berit-select__trigger');
      const list = el.querySelector('.berit-select__list');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (list) list.hidden = true;
    });
  }

  // Close on a click that lands outside every dropdown, or on Escape.
  // (Checking the click target directly is more reliable than relying on
  // stopPropagation, which was causing dropdowns to flicker / reclose.)
  document.addEventListener('click', (event) => {
    if (event.target.closest('.berit-select')) return;
    closeAll();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAll();
  });

  // Build one custom dropdown for a given native-select wrapper.
  // openNames is a Set of native-select names that were open before this
  // rebuild, so we can reopen the matching fresh dropdown (see buildAll).
  function buildOne(wrapper, openNames) {
    const select = wrapper.querySelector('select');
    if (!select) return;

    // Remove any custom dropdown left from a previous build, then mark the
    // wrapper so CSS hides the native select. Work out whether this dropdown
    // was open before the rebuild: Dawn often discards the whole wrapper on a
    // selection, so we can't rely on a flag inside this wrapper alone — we also
    // check the picker-wide list of open names captured in buildAll.
    const previous = wrapper.querySelector('[data-berit-select]');
    const wasOpen =
      (openNames && openNames.has(select.name)) ||
      (previous ? previous.classList.contains('is-open') : false);
    if (previous) previous.remove();
    wrapper.classList.add('berit-enhanced');

    const custom = document.createElement('div');
    custom.className = 'berit-select';
    custom.setAttribute('data-berit-select', '');

    // ----- trigger (the closed state) -----
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'berit-select__trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');

    const current = document.createElement('span');
    current.className = 'berit-select__current';
    const selectedOption = select.selectedOptions[0];
    current.textContent = selectedOption ? selectedOption.value : '';

    const caret = document.createElement('span');
    caret.className = 'berit-select__caret';
    caret.setAttribute('aria-hidden', 'true');
    // A small solid downward triangle (a classic select arrow), giving the
    // dropdowns a touch more weight than the old thin chevron.
    caret.innerHTML =
      '<svg class="berit-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="8 10 16 10 12 15"></polygon></svg>';

    trigger.append(current, caret);

    // ----- list (the open state) -----
    const list = document.createElement('ul');
    list.className = 'berit-select__list';
    list.setAttribute('role', 'listbox');
    list.hidden = true;

    Array.from(select.options).forEach((option) => {
      const available = option.dataset.available !== 'false';

      const li = document.createElement('li');
      const optionButton = document.createElement('button');
      optionButton.type = 'button';
      optionButton.className = 'berit-select__option';
      optionButton.setAttribute('role', 'option');
      if (option.selected) optionButton.setAttribute('aria-selected', 'true');
      if (!available) optionButton.classList.add('is-unavailable');

      const name = document.createElement('span');
      name.className = 'berit-select__option-name';
      name.textContent = option.value;

      optionButton.append(name);

      // Only sold-out choices carry a status; an available size needs no label.
      if (!available) {
        const status = document.createElement('span');
        status.className = 'berit-select__option-status';
        status.textContent = 'join waitlist';
        optionButton.append(status);
      }

      optionButton.addEventListener('click', () => {
        // Drive Dawn's real select, then let Dawn react to the change.
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        current.textContent = option.value;
        closeAll();
      });

      li.appendChild(optionButton);
      list.appendChild(li);
    });

    // ----- open / close -----
    function openThis() {
      custom.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      list.hidden = false;
    }

    trigger.addEventListener('click', () => {
      const isOpen = custom.classList.contains('is-open');
      closeAll();
      if (!isOpen) openThis();
    });

    custom.append(trigger, list);
    wrapper.appendChild(custom);

    // If a rebuild replaced an open dropdown, reopen the fresh one so it doesn't
    // appear to slam shut under the shopper.
    if (wasOpen) openThis();
  }

  function buildAll(root) {
    // Before rebuilding, note which dropdowns are currently open, keyed by their
    // native select's name (e.g. 'options[Size]'). The name survives Dawn
    // swapping in a brand-new wrapper, so we can reopen the right fresh dropdown
    // instead of letting it slam shut under the shopper.
    const openNames = new Set();
    root.querySelectorAll(WRAPPER_SELECTOR).forEach((wrapper) => {
      const custom = wrapper.querySelector('[data-berit-select]');
      const select = wrapper.querySelector('select');
      if (select && custom && custom.classList.contains('is-open')) {
        openNames.add(select.name);
      }
    });

    root.querySelectorAll(WRAPPER_SELECTOR).forEach((wrapper) => buildOne(wrapper, openNames));
  }

  // Wire the mobile carousel arrows: each one scrolls the image strip by a
  // single frame, and they fade out at the start / end. (On desktop the
  // arrows are hidden by CSS, so this quietly does nothing there.)
  function setupCarousel(productInfo) {
    const media = productInfo.querySelector('.berit-product__media');
    const prev = productInfo.querySelector('.berit-product__arrow--prev');
    const next = productInfo.querySelector('.berit-product__arrow--next');
    if (!media || !prev || !next) return;

    function step() {
      const frame = media.querySelector('.berit-product__frame');
      const gap = parseFloat(getComputedStyle(media).gap) || 0;
      return frame ? frame.getBoundingClientRect().width + gap : media.clientWidth;
    }

    function updateArrows() {
      const maxScroll = media.scrollWidth - media.clientWidth;
      prev.disabled = media.scrollLeft <= 1;
      next.disabled = media.scrollLeft >= maxScroll - 1;
    }

    // Respect the visitor's reduced-motion setting: jump, don't glide.
    const scrollBehavior = () =>
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    prev.addEventListener('click', () => {
      media.scrollBy({ left: -step(), behavior: scrollBehavior() });
    });
    next.addEventListener('click', () => {
      media.scrollBy({ left: step(), behavior: scrollBehavior() });
    });

    media.addEventListener('scroll', updateArrows, { passive: true });

    // The very first measurement can run before the images have loaded and
    // laid out, when the strip looks like it has nothing to scroll to (which
    // wrongly hides the 'next' arrow on landing). Re-measure once layout
    // settles, as each image loads, and on resize.
    if ('ResizeObserver' in window) {
      new ResizeObserver(updateArrows).observe(media);
    }
    media.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', updateArrows, { once: true });
    });
    window.addEventListener('load', updateArrows);
    window.addEventListener('resize', updateArrows);

    updateArrows();
  }

  // Click any product image to view it full-size on a quiet Bone field. A
  // single overlay is built once and reused for every image, with prev/next
  // arrows to move through the set. Click the backdrop or image, the close
  // button, or press Escape to dismiss; left/right arrow keys step through.
  function setupLightbox(productInfo) {
    const images = Array.from(productInfo.querySelectorAll('.berit-product__image'));
    if (!images.length) return;

    let overlay = null;
    let overlayImage = null;
    let prevBtn = null;
    let nextBtn = null;
    let index = 0;

    // Ask Shopify's image CDN for a larger copy than the page version, so the
    // full-screen view stays crisp. (The src carries a '&width=' we can bump;
    // if it's missing we just use the image as-is.)
    function fullSize(img) {
      const src = img.getAttribute('src') || img.currentSrc || '';
      return src.replace(/([?&]width=)\d+/, '$12048');
    }

    // A solid (filled) triangle arrow button, matching the dropdowns and the
    // footer submit so every arrow on the site speaks the same language.
    function makeArrow(direction) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'berit-lightbox__arrow berit-lightbox__arrow--' + direction;
      button.setAttribute('aria-label', direction === 'prev' ? 'Previous image' : 'Next image');
      const points = direction === 'prev' ? '16 5 16 19 7 12' : '8 5 8 19 17 12';
      button.innerHTML =
        '<svg class="berit-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="' +
        points +
        '"></polygon></svg>';
      return button;
    }

    // Show the image at the given position and fade the arrows out at the ends.
    function showImage(next) {
      index = next;
      overlayImage.src = fullSize(images[index]);
      overlayImage.alt = images[index].alt || '';
      if (prevBtn) prevBtn.disabled = index <= 0;
      if (nextBtn) nextBtn.disabled = index >= images.length - 1;
    }

    function closeLightbox() {
      if (!overlay) return;
      overlay.hidden = true;
      overlayImage.removeAttribute('src');
      document.body.style.overflow = ''; // let the page scroll again
    }

    function build() {
      overlay = document.createElement('div');
      overlay.className = 'berit-lightbox';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.hidden = true;

      overlayImage = document.createElement('img');
      overlayImage.className = 'berit-lightbox__image';
      overlayImage.alt = '';
      // Clicking the photo itself does nothing, so looking closely never closes
      // the view; only the surrounding backdrop, the × or Escape close it.
      overlayImage.addEventListener('click', (event) => event.stopPropagation());

      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'berit-lightbox__close';
      close.setAttribute('aria-label', 'Close');
      close.textContent = 'close';
      close.addEventListener('click', closeLightbox);

      overlay.append(overlayImage, close);

      // Arrows only earn their place when there's more than one image. They
      // stop the click bubbling so stepping through doesn't also close the view.
      if (images.length > 1) {
        prevBtn = makeArrow('prev');
        nextBtn = makeArrow('next');
        prevBtn.addEventListener('click', (event) => {
          event.stopPropagation();
          if (index > 0) showImage(index - 1);
        });
        nextBtn.addEventListener('click', (event) => {
          event.stopPropagation();
          if (index < images.length - 1) showImage(index + 1);
        });
        overlay.append(prevBtn, nextBtn);
      }

      document.body.appendChild(overlay);

      // A click on the surrounding backdrop closes the view (the image and
      // arrows above stop their own clicks from reaching here).
      overlay.addEventListener('click', closeLightbox);
    }

    function openLightbox(start) {
      if (!overlay) build();
      showImage(start);
      overlay.hidden = false;
      document.body.style.overflow = 'hidden'; // freeze the page behind the overlay
    }

    images.forEach((img, position) => {
      img.addEventListener('click', () => openLightbox(position));
    });

    document.addEventListener('keydown', (event) => {
      if (!overlay || overlay.hidden) return;
      if (event.key === 'Escape') closeLightbox();
      if (event.key === 'ArrowLeft' && index > 0) showImage(index - 1);
      if (event.key === 'ArrowRight' && index < images.length - 1) showImage(index + 1);
    });
  }

  // The waiting list: shown in place of add-to-cart while the chosen size is
  // out of stock. Submitting sends the email, the selected variant and the
  // visitor's country to Klaviyo (see berit-klaviyo.js), which drives both
  // the confirmation email and the restock notification.
  function setupWaitlist(productInfo) {
    const buy = productInfo.querySelector('.berit-product__buy');
    if (!buy) return;

    const buyWrap = buy.querySelector('[data-berit-buy]');
    const waitlist = buy.querySelector('[data-berit-waitlist]');
    const openBtn = buy.querySelector('[data-berit-waitlist-open]');
    const panel = buy.querySelector('[data-berit-waitlist-panel]');
    const form = waitlist ? waitlist.querySelector('form') : null;
    const emailInput = form ? form.querySelector('input[type="email"]') : null;
    if (!waitlist) return;

    // Only flip an element's hidden state when it actually changes, so toggling
    // visibility doesn't retrigger the observer below in a loop.
    function setHidden(el, value) {
      if (el && el.hidden !== value) el.hidden = value;
    }

    // Dawn marks the add-to-cart button as disabled when the chosen variant
    // can't be bought. Use that as the single source of truth for which block
    // to show. (Re-query the button each time: Dawn replaces it on each change.)
    //
    // While a new selection loads, Dawn also disables the button but leaves
    // its "add to cart" wording in place; a genuinely sold-out variant gets
    // the sold-out wording. Skip that loading moment, or every size change
    // would flash the waitlist before the button comes back. The loading class
    // also keeps the button black for that moment instead of the sand
    // disabled style.
    function sync() {
      const submit = buy.querySelector('.product-form__submit');
      const disabled = !submit || submit.hasAttribute('disabled');
      const label = submit ? (submit.querySelector('span') || submit).textContent.trim() : '';
      const addToCart = (window.variantStrings && window.variantStrings.addToCart) || '';
      const loading = disabled && addToCart && label === addToCart.trim();
      buy.classList.toggle('is-variant-loading', Boolean(loading));
      if (loading) return;
      const soldOut = disabled;
      setHidden(buyWrap, soldOut);
      setHidden(waitlist, !soldOut);
    }

    // Swap the button for the email panel (step 1 -> step 2).
    function openPanel(focus) {
      setHidden(openBtn, true);
      setHidden(panel, false);
      if (focus && emailInput) emailInput.focus();
    }

    if (openBtn) {
      openBtn.addEventListener('click', () => openPanel(true));
    }

    // Swap the form for the thank-you line once a sign-up succeeds; the
    // spent instructions, field and consent would otherwise stack above it.
    function showSignedUp() {
      waitlist.querySelectorAll('[data-berit-hide-on-success]').forEach((el) => {
        setHidden(el, true);
      });
    }

    // Bring the form back (used when the panel is reset), ready for a new
    // selection or another address.
    function resetPanel() {
      waitlist.querySelectorAll('[data-berit-hide-on-success]').forEach((el) => {
        setHidden(el, false);
      });
      waitlist
        .querySelectorAll('[data-berit-note-success], [data-berit-note-invalid], [data-berit-note-error]')
        .forEach((el) => setHidden(el, true));
    }

    // Collapse back to the button (step 2 -> step 1).
    function closePanel() {
      setHidden(openBtn, false);
      setHidden(panel, true);
      resetPanel();
    }

    // Changing the size or model resets to the button, so each new selection
    // starts from the button rather than leaving the email field open from a
    // previous pick. (Listening on the wrapper keeps working even after Dawn
    // re-renders the dropdowns.)
    const pickers = productInfo.querySelector('.berit-product__pickers');
    if (pickers) pickers.addEventListener('change', closePanel);

    // On submit: one Klaviyo list subscription (no page reload), carrying the
    // visitor's country and the selected variant id as custom profile
    // properties. Joining the list drives the confirmation email; restock
    // emails are sent from Klaviyo by segmenting on variant_id.
    // Resubmitting is allowed, so no button disabling.
    if (form) {
      const noteSuccess = form.querySelector('[data-berit-note-success]');
      const noteInvalid = form.querySelector('[data-berit-note-invalid]');
      const noteError = form.querySelector('[data-berit-note-error]');

      function showNote(note) {
        [noteSuccess, noteInvalid, noteError].forEach((n) => {
          if (n) n.hidden = n !== note;
        });
      }

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const klaviyo = window.BeritKlaviyo;
        const email = emailInput ? emailInput.value : '';

        if (!klaviyo) {
          console.error('[berit-klaviyo] berit-klaviyo.js is not loaded.');
          showNote(noteError);
          return;
        }
        if (!klaviyo.validEmail(email)) {
          showNote(noteInvalid);
          return;
        }

        // Dawn keeps this hidden input in the buy form synced to the
        // currently selected variant.
        const idInput = buy.querySelector('[name="id"]');
        const variantId = idInput ? idInput.value : '';

        // Alongside the variant id, send the chosen options as readable
        // properties (e.g. oura_model: "Oura ring 4", oura_size: "8"), so
        // demand can be read and segmented in Klaviyo at a glance.
        const properties = { variant_id: variantId };
        productInfo.querySelectorAll('.berit-product__pickers select').forEach((select) => {
          const match = select.name.match(/options\[(.+)\]/);
          if (match && select.value) {
            const key = match[1].toLowerCase().replace(/[^a-z0-9]+/g, '_');
            properties[key] = select.value;
          }
        });

        klaviyo
          .subscribe(email, 'BERIT product waitlist', properties)
          .then(() => {
            showSignedUp();
            // Echo the typed address so typos are caught on sight.
            const slot = noteSuccess ? noteSuccess.querySelector('[data-berit-email]') : null;
            if (slot) slot.textContent = email.trim();
            showNote(noteSuccess);
          })
          .catch((error) => {
            console.error('[berit-klaviyo]', error);
            showNote(noteError);
          });
      });
    }

    // Dawn re-renders the buy area after each selection; re-check when it does.
    const observer = new MutationObserver(sync);
    observer.observe(buy, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled'],
    });

    sync();
  }

  function init(productInfo) {
    setupCarousel(productInfo);
    setupWaitlist(productInfo);
    setupLightbox(productInfo);

    const pickers = productInfo.querySelector('.berit-product__pickers');
    if (!pickers) return;

    buildAll(pickers);

    // Dawn replaces the variant area after each selection. Rebuild the custom
    // dropdowns when fresh variant markup is ADDED. We deliberately ignore
    // removals: Dawn swaps the variants with a fade, so the old block lingers
    // and is removed a moment later. Reacting to that late removal would fire a
    // second rebuild that could destroy a dropdown the shopper just reopened
    // (the 'instantly closes' flicker).
    const observer = new MutationObserver((mutations) => {
      const selectAdded = mutations.some((mutation) =>
        [...mutation.addedNodes].some(
          (node) =>
            node.nodeType === 1 &&
            (node.matches?.('variant-selects, select') || node.querySelector?.('select'))
        )
      );
      if (!selectAdded) return;

      observer.disconnect();
      buildAll(pickers);
      observer.observe(pickers, { childList: true, subtree: true });
    });

    observer.observe(pickers, { childList: true, subtree: true });
  }

  function start() {
    document.querySelectorAll('product-info').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
